<template>
  <section class="publications py-5">
    <div class="container">
      <div class="row">

        <!-- SIDEBAR ANNEES -->
        <div class="col-md-4">
          <div class="sidebar">
            <div class="">
              <h4 class="widget-title">Années</h4>

              <ul class="category-list">
                <li
                  v-for="year in uniqueYears"
                  :key="year"
                  @click="selectYear(year)"
                  :class="{ active: selectedYear === year }"
                >
                  <span>{{ year }}</span>
                  <i class="ri-arrow-right-s-line"></i>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- LISTE DES TEXTES -->
        <div class="col-md-8">
          <div class="content-box">
            <h4 class="mb-4">
              Textes - {{ selectedYear }}
            </h4>

            <ul class="text-list">
              <li
                v-for="pub in filteredPublications"
                :key="pub.id"
                @click="goToDetail(pub.id)"
              >
                {{ pub.titre }}
              </li>
            </ul>

            <div v-if="filteredPublications.length === 0">
              Aucun texte disponible.
            </div>
          </div>
        </div>

      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import ApiService from "@/services/ApiService";

interface Publication {
  id: number;
  titre: string;
  auteur: string;
  editeur: string;
  categoriepublication: string;
  urlImage: string;
  lieu: string;
  annepublication: number;
  contenu: string;
}

export default defineComponent({
  name: "LesPublications",

  setup() {
    const router = useRouter();

    const publications = ref<Publication[]>([]);
    const selectedYear = ref<number | null>(null);

    const getPublications = async (pageNum = 1, limit = 100) => {
      try {
        const { data } = await ApiService.get(
          `/publications/categorie/Textes?page=${pageNum}&limit=${limit}`
        );

        publications.value = data.data.data;

      } catch (err) {
        console.error(err);
      }
    };

    // Liste unique des années triées (plus récente en premier)
    const uniqueYears = computed(() => {
      const years = publications.value.map((p) => p.annepublication);
      return Array.from(new Set(years)).sort((a, b) => b - a);
    });

    // Publications filtrées
    const filteredPublications = computed(() => {
      if (!selectedYear.value) return [];
      return publications.value.filter(
        (p) => p.annepublication === selectedYear.value
      );
    });

    const selectYear = (year: number) => {
      selectedYear.value = year;
    };

    const goToDetail = (id: number) => {
      router.push({ name: "LesTextesDetails", params: { id } });
    };

    onMounted(async () => {
      await getPublications();

      // Sélection automatique de la première année
      if (uniqueYears.value.length > 0) {
        selectedYear.value = uniqueYears.value[0];
      }
    });

    return {
      uniqueYears,
      selectedYear,
      filteredPublications,
      selectYear,
      goToDetail,
    };
  },
});
</script>

<style scoped>

/* SIDEBAR */
.sidebar {
  background: #ffffff;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
}

.widget-title {
  font-weight: 600;
  margin-bottom: 20px;
}

.category-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.category-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 17px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f5f5f5;  /* gris clair */
  color:black;          /* texte gris */
  margin-bottom: 10px;
}

.category-list li:hover {
  background: #ffc107;
  transform: translateX(5px);
}

.category-list li.active {
  background: #ffc107;
  color: #555;
}

.category-list li i {
  font-size: 18px;
}

/* CONTENU */
.content-box {
  background: #ffffff;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
}

.text-list {
  list-style: none;
  font-size: 18px;
  padding: 0;
}

.text-list li {
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: all 0.3s ease;
}

.text-list li:hover {
  color: #fec329;
  padding-left: 10px;
}

</style>
